import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Phone, MessageCircle, Shield, Mail, Code2 } from "lucide-react";
import ContactModal from "@/components/ContactModal";

const DISCORD_URL = "https://discord.gg/nQKVtqFMsv";
const CREATOR = "Marwane M.";

export default function Footer() {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <footer className="relative border-t border-white/5 bg-[#070C18]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-emerald-500 grid place-items-center text-white font-bold">
                LÉ
              </span>
              <span className="font-[Outfit] font-semibold text-lg text-white">
                L'Équipe & Mental
              </span>
            </div>
            <p className="mt-4 text-slate-400 max-w-md">
              Un espace communautaire pour le mental, les passions et les projets. On avance
              à ton rythme, on t'écoute sans juger.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-rose-300">
              <Heart className="w-4 h-4" /> Prends soin de toi — tu comptes.
            </p>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-white font-semibold font-[Outfit]">Urgences — 24h/24</h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <a href="tel:3114" data-testid="footer-emergency-3114" className="inline-flex items-center gap-2 text-slate-200 hover:text-orange-300">
                  <Phone className="w-3.5 h-3.5" /> 3114 — Prévention du suicide
                </a>
              </li>
              <li>
                <a href="tel:114" data-testid="footer-emergency-114" className="inline-flex items-center gap-2 text-slate-200 hover:text-orange-300">
                  <Phone className="w-3.5 h-3.5" /> 114 — SMS d'urgence
                </a>
              </li>
              <li>
                <a href="tel:15" data-testid="footer-emergency-15" className="inline-flex items-center gap-2 text-slate-200 hover:text-orange-300">
                  <Phone className="w-3.5 h-3.5" /> 15 — SAMU
                </a>
              </li>
              <li>
                <a href="tel:112" data-testid="footer-emergency-112" className="inline-flex items-center gap-2 text-slate-200 hover:text-orange-300">
                  <Phone className="w-3.5 h-3.5" /> 112 — Numéro européen
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-white font-semibold font-[Outfit]">La communauté</h4>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-discord"
              className="mt-4 inline-flex items-center gap-2 bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-400/30 text-indigo-100 font-medium px-4 py-2.5 rounded-full"
            >
              <MessageCircle className="w-4 h-4" />
              Serveur Discord
            </a>
            <button
              onClick={() => setContactOpen(true)}
              data-testid="footer-contact-btn"
              className="mt-3 inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium px-4 py-2.5 rounded-full text-sm"
            >
              <Mail className="w-4 h-4 text-orange-300" />
              Me contacter en privé
            </button>
            <p className="mt-5 text-xs text-slate-500 leading-relaxed">
              Les contenus de ce site sont destinés au soutien amical et à la motivation. Ils
              ne remplacent pas un suivi médical ou psychologique.
            </p>
          </div>
        </div>

        {/* Creator credit */}
        <div className="mt-10 pt-6 border-t border-white/5">
          <div
            className="rounded-2xl border border-white/10 p-5 lg:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            style={{
              background:
                "linear-gradient(90deg, rgba(249, 115, 22, 0.08), rgba(16, 185, 129, 0.08))",
            }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-emerald-500 grid place-items-center text-white font-bold shadow-lg shadow-orange-500/30">
                <Code2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs em-eyebrow">Créé avec le cœur par</p>
                <p className="text-white font-semibold text-lg font-[Outfit]" data-testid="footer-creator">
                  {CREATOR}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Pour tous ceux qui en ont besoin, un message, un soir.
                </p>
              </div>
            </div>
            <button
              onClick={() => setContactOpen(true)}
              data-testid="footer-creator-contact"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-full shadow-lg shadow-orange-500/20"
            >
              <Mail className="w-4 h-4" />
              Me contacter en privé
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} L'Équipe & Mental — Un espace bienveillant, par et pour la communauté.</p>
          <div className="flex items-center gap-3">
            <span>Mentions légales · Confidentialité · Modération respectueuse</span>
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white"
            >
              <Shield className="w-3.5 h-3.5 text-orange-400" />
              Accès admin
            </Link>
          </div>
        </div>
      </div>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </footer>
  );
}
